import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminAds,
  saveAd,
  deleteAd,
  getAdminAdPlacements,
  saveAdPlacement,
  deleteAdPlacement,
  type AdPayload,
  type AdPlacementPayload,
} from "@/services/adminService";
import type { AdminAd, AdminAdPlacement } from "@/types/admin";
import ImageUploader from "@/components/base/ImageUploader";
import { adBannerRule } from "@/utils/imageValidation";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import MediaPickerModal from "@/pages/portal/admin/components/MediaPickerModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
  statusBadge,
} from "@/pages/portal/admin/config/ui";

interface AdsSectionProps {
  kind: "ads" | "placements";
}

const EMPTY_AD: AdPayload = { title: "", placementSlug: "", targetUrl: "", imageUrl: "", categoryName: "", status: "active", startsAt: "", endsAt: "" };
const EMPTY_PLACEMENT: AdPlacementPayload = { name: "", slug: "", description: "", width: 970, height: 250, isActive: true };

function slugify(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdsSection({ kind }: AdsSectionProps) {
  const { accessToken } = useAuth();
  const isAds = kind === "ads";

  const { data: ads, setData: setAds, loading: adsLoading, error: adsError, reload: adsReload } = useAdminResource(
    () => getAdminAds(accessToken), [] as AdminAd[], [accessToken],
  );
  const { data: placements, setData: setPlacements, loading: plLoading, error: plError, reload: plReload } = useAdminResource(
    () => getAdminAdPlacements(accessToken), [] as AdminAdPlacement[], [accessToken],
  );

  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [adForm, setAdForm] = useState<AdPayload | null>(null);
  const [plForm, setPlForm] = useState<AdPlacementPayload | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const loading = isAds ? adsLoading : plLoading;
  const error   = isAds ? adsError : plError;
  const reload  = isAds ? adsReload : plReload;
  /* eslint-enable @typescript-eslint/no-unused-vars */

  const selectedPlacement = placements.find((p) => p.slug === adForm?.placementSlug);
  const bannerRule = adBannerRule(selectedPlacement?.width ?? 970, selectedPlacement?.height ?? 250);

  const filterAds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ads;
    return ads.filter((a) => [a.title, a.placement, a.categoryName ?? ""].join(" ").toLowerCase().includes(q));
  }, [ads, search]);

  const filterPlacements = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return placements;
    return placements.filter((p) => [p.name, p.slug, p.description].join(" ").toLowerCase().includes(q));
  }, [placements, search]);

  const totalImpressions = ads.reduce((s, a) => s + a.impressions, 0);
  const totalClicks      = ads.reduce((s, a) => s + a.clicks, 0);

  async function submitAd() {
    if (!adForm?.title?.trim()) return;
    const payload = { ...adForm };
    setBusyId(editId ?? -1);
    try {
      await saveAd(accessToken, payload, editId ?? undefined);
      if (editId) {
        setAds((prev) => prev.map((a) => (a.id === editId ? { ...a, ...payload } : a)));
      } else {
        setAds((prev) => [
          {
            id: Date.now(), title: payload.title as string, placementSlug: payload.placementSlug ?? "",
            placement: placements.find((p) => p.slug === payload.placementSlug)?.name ?? "—",
            targetUrl: payload.targetUrl ?? "", imageUrl: payload.imageUrl ?? "",
            categoryName: payload.categoryName || null, status: (payload.status as AdminAd["status"]) ?? "active",
            impressions: 0, clicks: 0, startsAt: payload.startsAt ?? new Date().toISOString(),
            endsAt: payload.endsAt ?? new Date(Date.now() + 90 * 86400000).toISOString(),
          },
          ...prev,
        ]);
      }
      setAdForm(null);
      setEditId(null);
    } finally {
      setBusyId(null);
    }
  }

  async function submitPlacement() {
    if (!plForm?.name?.trim()) return;
    const payload: AdPlacementPayload = { ...plForm, slug: plForm.slug?.trim() || slugify(plForm.name) };
    setBusyId(editId ?? -1);
    try {
      await saveAdPlacement(accessToken, payload, editId ?? undefined);
      if (editId) {
        setPlacements((prev) => prev.map((p) => (p.id === editId ? { ...p, ...payload, slug: payload.slug as string } : p)));
      } else {
        setPlacements((prev) => [
          ...prev,
          {
            id: Date.now(), name: payload.name as string, slug: payload.slug as string,
            description: payload.description ?? "", width: payload.width ?? 970, height: payload.height ?? 250,
            adCount: 0, isActive: payload.isActive ?? true,
          },
        ]);
      }
      setPlForm(null);
      setEditId(null);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleAdStatus(a: AdminAd) {
    const next = a.status === "active" ? "paused" : "active";
    setAds((prev) => prev.map((row) => (row.id === a.id ? { ...row, status: next } : row)));
    await saveAd(accessToken, { status: next }, a.id);
  }

  async function togglePlacement(p: AdminAdPlacement) {
    const next = !p.isActive;
    setPlacements((prev) => prev.map((row) => (row.id === p.id ? { ...row, isActive: next } : row)));
    await saveAdPlacement(accessToken, { isActive: next }, p.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      if (isAds) {
        await deleteAd(accessToken, deleteTarget.id);
        setAds((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      } else {
        await deleteAdPlacement(accessToken, deleteTarget.id);
        setPlacements((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      }
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder={isAds ? "Search advertisements…" : "Search placements…"} />
        <button
          type="button"
          onClick={() => {
            setEditId(null);
            if (isAds) setAdForm({ ...EMPTY_AD, placementSlug: placements[0]?.slug ?? "" });
            else setPlForm({ ...EMPTY_PLACEMENT });
          }}
          className={adminBtnPrimary}
        >
          <i className="ri-add-line text-sm" /> {isAds ? "New advertisement" : "New placement"}
        </button>
        {isAds && (
          <span className="ml-auto text-xs text-foreground-500">
            {totalImpressions.toLocaleString("en-IN")} impressions · {totalClicks.toLocaleString("en-IN")} clicks
          </span>
        )}
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : isAds ? (
          filterAds.length === 0 ? (
            <EmptyBlock icon="ri-advertisement-line" text="No advertisements yet" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse">
                <thead className="border-b border-background-200">
                  <tr>
                    <th className={adminTh}>Advertisement</th>
                    <th className={adminTh}>Placement</th>
                    <th className={adminTh}>Impressions</th>
                    <th className={adminTh}>Clicks</th>
                    <th className={adminTh}>CTR</th>
                    <th className={adminTh}>Status</th>
                    <th className={`${adminTh} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filterAds.map((a) => {
                    const ctr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(2) : "0.00";
                    return (
                      <tr key={a.id} className="hover:bg-background-100/40">
                        <td className={adminTd}>
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
                              {a.imageUrl
                                ? <img src={a.imageUrl} alt={a.title} className="h-full w-full object-cover" />
                                : <i className="ri-image-line text-foreground-400" />}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-foreground-900">{a.title}</p>
                              {a.categoryName && <p className="truncate text-[11px] text-foreground-400">{a.categoryName}</p>}
                            </div>
                          </div>
                        </td>
                        <td className={`${adminTd} text-xs`}>{a.placement}</td>
                        <td className={`${adminTd} tabular-nums`}>{a.impressions.toLocaleString("en-IN")}</td>
                        <td className={`${adminTd} tabular-nums`}>{a.clicks.toLocaleString("en-IN")}</td>
                        <td className={`${adminTd} tabular-nums`}>{ctr}%</td>
                        <td className={adminTd}>
                          <button type="button" disabled={busyId === a.id} onClick={() => toggleAdStatus(a)} className={`cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-bold capitalize transition-colors ${statusBadge(a.status)}`}>
                            {a.status}
                          </button>
                        </td>
                        <td className={`${adminTd} text-right`}>
                          <div className="flex items-center justify-end gap-1">
                            <a href={a.targetUrl} target="_blank" rel="noopener noreferrer" className={adminIconBtn} title="Open target link"><i className="ri-external-link-line text-base" /></a>
                            <button type="button" onClick={() => { setEditId(a.id); setAdForm({ title: a.title, placementSlug: a.placementSlug, targetUrl: a.targetUrl, imageUrl: a.imageUrl, categoryName: a.categoryName ?? "", status: a.status, startsAt: a.startsAt, endsAt: a.endsAt }); }} className={adminIconBtn} title="Edit"><i className="ri-pencil-line text-base" /></button>
                            <button type="button" onClick={() => setDeleteTarget({ id: a.id, name: a.title })} className={adminIconBtn} title="Delete"><i className="ri-delete-bin-line text-base text-rose-600" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : filterPlacements.length === 0 ? (
          <EmptyBlock icon="ri-layout-masonry-line" text="No ad placements yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Placement</th>
                  <th className={adminTh}>Slug</th>
                  <th className={adminTh}>Size</th>
                  <th className={adminTh}>Ads</th>
                  <th className={adminTh}>Visible</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filterPlacements.map((p) => (
                  <tr key={p.id} className="hover:bg-background-100/40">
                    <td className={adminTd}>
                      <p className="font-semibold text-foreground-900">{p.name}</p>
                      <p className="text-[11px] text-foreground-400">{p.description}</p>
                    </td>
                    <td className={adminTd}><code className="rounded bg-background-100 px-1.5 py-0.5 text-[11px] text-foreground-600">{p.slug}</code></td>
                    <td className={`${adminTd} tabular-nums`}>{p.width} × {p.height}</td>
                    <td className={`${adminTd} tabular-nums`}>{p.adCount}</td>
                    <td className={adminTd}>
                      <button type="button" onClick={() => togglePlacement(p)} className={`cursor-pointer rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${p.isActive ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-background-200 text-foreground-600 hover:bg-background-300"}`}>
                        {p.isActive ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => { setEditId(p.id); setPlForm({ name: p.name, slug: p.slug, description: p.description, width: p.width, height: p.height, isActive: p.isActive }); }} className={adminIconBtn} title="Edit"><i className="ri-pencil-line text-base" /></button>
                        <button type="button" onClick={() => setDeleteTarget({ id: p.id, name: p.name })} className={adminIconBtn} title="Delete"><i className="ri-delete-bin-line text-base text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ad create/edit modal */}
      {adForm && (
        <AdminModal
          title={editId ? "Edit advertisement" : "New advertisement"}
          onClose={() => { setAdForm(null); setEditId(null); }}
          wide
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setAdForm(null); setEditId(null); }} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!adForm.title?.trim() || busyId !== null} onClick={submitAd} className={adminBtnPrimary}>
                {editId ? "Save changes" : "Create advertisement"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={adminLabel} htmlFor="ad-title">Title</label>
              <input id="ad-title" type="text" value={adForm.title ?? ""} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} className={adminInput} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="ad-placement">Placement</label>
                <select id="ad-placement" value={adForm.placementSlug ?? ""} onChange={(e) => setAdForm({ ...adForm, placementSlug: e.target.value })} className={`${adminInput} cursor-pointer`}>
                  <option value="">Select placement…</option>
                  {placements.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className={adminLabel} htmlFor="ad-status">Status</label>
                <select id="ad-status" value={adForm.status ?? "active"} onChange={(e) => setAdForm({ ...adForm, status: e.target.value as AdPayload["status"] })} className={`${adminInput} cursor-pointer`}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
            <div>
              <label className={adminLabel} htmlFor="ad-target">Target URL</label>
              <input id="ad-target" type="url" value={adForm.targetUrl ?? ""} onChange={(e) => setAdForm({ ...adForm, targetUrl: e.target.value })} placeholder="https://" className={adminInput} />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className={`${adminLabel} mb-0`}>Banner image</span>
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  className={adminBtnGhost}
                >
                  <i className="ri-image-2-line text-sm" /> Choose from Media Library
                </button>
              </div>
              <ImageUploader
                value={adForm.imageUrl ? [adForm.imageUrl] : []}
                onChange={(urls) => setAdForm({ ...adForm, imageUrl: urls[0] ?? "" })}
                rule={bannerRule}
                folder="ads"
                token={accessToken}
                variant="banner"
              />
              <p className="mt-1.5 text-[11px] text-foreground-500">
                {selectedPlacement
                  ? `Banners for “${selectedPlacement.name}” must be ${selectedPlacement.width} × ${selectedPlacement.height} px.`
                  : "Pick a placement to see the required banner size."}
              </p>
            </div>
            <div>
              <label className={adminLabel} htmlFor="ad-cat">Category (optional)</label>
              <input id="ad-cat" type="text" value={adForm.categoryName ?? ""} onChange={(e) => setAdForm({ ...adForm, categoryName: e.target.value })} className={adminInput} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="ad-start">Starts at</label>
                <input id="ad-start" type="date" value={(adForm.startsAt ?? "").slice(0, 10)} onChange={(e) => setAdForm({ ...adForm, startsAt: e.target.value })} className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="ad-end">Ends at</label>
                <input id="ad-end" type="date" value={(adForm.endsAt ?? "").slice(0, 10)} onChange={(e) => setAdForm({ ...adForm, endsAt: e.target.value })} className={adminInput} />
              </div>
            </div>
          </div>
        </AdminModal>
      )}

      {showMediaPicker && (
        <MediaPickerModal
          token={accessToken}
          currentUrl={adForm?.imageUrl}
          onSelect={(url) => setAdForm((prev) => (prev ? { ...prev, imageUrl: url } : prev))}
          onClose={() => setShowMediaPicker(false)}
        />
      )}

      {/* Placement create/edit modal */}
      {plForm && (
        <AdminModal
          title={editId ? "Edit placement" : "New placement"}
          onClose={() => { setPlForm(null); setEditId(null); }}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setPlForm(null); setEditId(null); }} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!plForm.name?.trim() || busyId !== null} onClick={submitPlacement} className={adminBtnPrimary}>
                {editId ? "Save changes" : "Create placement"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={adminLabel} htmlFor="pl-name">Name</label>
              <input id="pl-name" type="text" value={plForm.name ?? ""} onChange={(e) => setPlForm({ ...plForm, name: e.target.value })} className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="pl-slug">Slug</label>
              <input id="pl-slug" type="text" value={plForm.slug ?? ""} onChange={(e) => setPlForm({ ...plForm, slug: e.target.value })} placeholder="auto-generated" className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="pl-desc">Description</label>
              <textarea id="pl-desc" rows={2} value={plForm.description ?? ""} onChange={(e) => setPlForm({ ...plForm, description: e.target.value })} className={`${adminInput} h-auto py-2.5`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={adminLabel} htmlFor="pl-w">Width (px)</label>
                <input id="pl-w" type="number" min={1} value={plForm.width ?? 970} onChange={(e) => setPlForm({ ...plForm, width: Number(e.target.value) || 1 })} className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="pl-h">Height (px)</label>
                <input id="pl-h" type="number" min={1} value={plForm.height ?? 250} onChange={(e) => setPlForm({ ...plForm, height: Number(e.target.value) || 1 })} className={adminInput} />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
              <input type="checkbox" checked={!!plForm.isActive} onChange={(e) => setPlForm({ ...plForm, isActive: e.target.checked })} className="h-4 w-4 cursor-pointer accent-primary-500" />
              Available for advertisements
            </label>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title={isAds ? "Delete advertisement" : "Delete placement"}
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
          </p>
        </AdminModal>
      )}
    </div>
  );
}

function EmptyBlock({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
        <i className={`${icon} text-2xl text-foreground-400`} />
      </div>
      <p className="text-sm font-medium text-foreground-700">{text}</p>
    </div>
  );
}